import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatFecha } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { MessageCard } from "@/components/MessageCard";
import { LatestPrayersRotator } from "@/components/LatestPrayersRotator";
import { BookOpen, HandHeart, Heart, ArrowRight, Sunrise, MessageCircleHeart } from "lucide-react";

export default function Home() {
  const [today, setToday] = useState(null);
  const [recent, setRecent] = useState([]);
  const [testimonios, setTestimonios] = useState([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    Promise.all([
      api.get("/messages/today"),
      api.get("/messages?limit=3"),
      api.get("/comments/recent?limit=4"),
    ])
      .then(([t, r, c]) => {
        setToday(t.data.message);
        setRecent(r.data.messages || []);
        setTestimonios(c.data.comments || []);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  return (
    <div>
      {/* Hero: Mensaje de Hoy */}
      <section className="relative overflow-hidden">
        <img
          src="/img/hero-sunrise.jpg"
          alt="Amanecer de esperanza"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-white/90 via-white/80 to-background" />
        <div className="noise-overlay" />
        <div className="relative mx-auto w-full max-w-6xl px-4 py-14 sm:px-6 sm:py-24 lg:px-8">
          <div className="mx-auto max-w-3xl text-center">
            <span className="inline-flex items-center gap-2 rounded-full bg-card px-4 py-1.5 text-xs font-semibold text-primary shadow-[var(--shadow-sm)]">
              <Sunrise className="h-4 w-4" /> Mensaje de Hoy
            </span>
            {loading ? (
              <div className="mt-6 space-y-4">
                <Skeleton className="mx-auto h-10 w-3/4" />
                <Skeleton className="mx-auto h-5 w-1/3" />
                <Skeleton className="mx-auto h-20 w-full" />
              </div>
            ) : today ? (
              <>
                <h1 className="mt-6 font-serif text-3xl font-semibold tracking-tight text-foreground sm:text-5xl" data-testid="home-today-title">
                  {today.titulo}
                </h1>
                <p className="mt-3 text-base font-semibold text-primary" data-testid="home-today-pasaje">
                  {today.pasaje} · {formatFecha(today.fecha)}
                </p>
                <p className="mx-auto mt-4 max-w-2xl text-base leading-relaxed text-foreground/80">
                  {(today.conexion || today.mensaje || "").split("\n\n")[0]?.slice(0, 220)}
                  {(today.conexion || today.mensaje || "").length > 220 ? "…" : ""}
                </p>
                <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
                  <Button size="lg" onClick={() => navigate(`/mensaje/${today.id}`)} data-testid="home-read-todays-message-button">
                    Leer mensaje completo <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                  <Button size="lg" variant="outline" className="bg-card" onClick={() => navigate("/oracion")} data-testid="home-prayer-wall-button">
                    <HandHeart className="mr-2 h-4 w-4" /> Muro de Oración
                  </Button>
                </div>
              </>
            ) : (
              <div className="mt-6">
                <h1 className="font-serif text-3xl font-semibold text-foreground sm:text-4xl">
                  Bienvenido/a al Centro de Fortaleza Espiritual
                </h1>
                <p className="mx-auto mt-4 max-w-xl text-base text-foreground/80" data-testid="home-empty-state">
                  Muy pronto publicaremos el primer mensaje de esperanza. Mientras tanto, recuerda: no estás solo/a. Dios está cerca.
                </p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Peticiones recientes del muro (rotador) */}
      <section className="mx-auto w-full max-w-4xl px-4 pt-8 sm:px-6" data-testid="home-latest-prayers-section">
        <LatestPrayersRotator showLink />
      </section>

      {/* Accesos rapidos */}
      <section className="mx-auto w-full max-w-6xl px-4 py-12 sm:px-6 sm:py-16 lg:px-8">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3 sm:gap-6">
          <Card className="group overflow-hidden border-border bg-card shadow-[var(--shadow-sm)] transition-shadow duration-300 hover:shadow-[var(--shadow-md)]">
            <div className="relative h-40 overflow-hidden">
              <img
                src="/img/bible.jpg"
                alt="Biblia abierta"
                loading="lazy"
                className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
              />
              <span className="absolute bottom-3 left-4 flex h-10 w-10 items-center justify-center rounded-full bg-card text-primary shadow-[var(--shadow-sm)]">
                <BookOpen className="h-5 w-5" />
              </span>
            </div>
            <CardContent className="flex flex-col gap-3 p-6">
              <h3 className="font-serif text-lg font-semibold">Archivo de mensajes</h3>
              <p className="text-sm text-muted-foreground">Explora los mensajes anteriores por fecha o tema: esperanza, consuelo, miedo, fortaleza.</p>
              <Link to="/archivo" className="inline-flex items-center gap-1 text-sm font-semibold text-primary hover:text-primary/80" data-testid="home-archivo-link">
                Ver archivo <ArrowRight className="h-4 w-4" />
              </Link>
            </CardContent>
          </Card>
          <Card className="group overflow-hidden border-border bg-card shadow-[var(--shadow-sm)] transition-shadow duration-300 hover:shadow-[var(--shadow-md)]">
            <div className="relative h-40 overflow-hidden">
              <img
                src="/img/community.jpg"
                alt="Comunidad unida en oración"
                loading="lazy"
                className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
              />
              <span className="absolute bottom-3 left-4 flex h-10 w-10 items-center justify-center rounded-full bg-card text-primary shadow-[var(--shadow-sm)]">
                <HandHeart className="h-5 w-5" />
              </span>
            </div>
            <CardContent className="flex flex-col gap-3 p-6">
              <h3 className="font-serif text-lg font-semibold">Muro de Oración</h3>
              <p className="text-sm text-muted-foreground">Comparte tu petición y ora por otros. Nadie debería atravesar esto solo.</p>
              <Link to="/oracion" className="inline-flex items-center gap-1 text-sm font-semibold text-primary hover:text-primary/80" data-testid="home-oracion-link">
                Ir al muro <ArrowRight className="h-4 w-4" />
              </Link>
            </CardContent>
          </Card>
          <Card className="group overflow-hidden border-border bg-card shadow-[var(--shadow-sm)] transition-shadow duration-300 hover:shadow-[var(--shadow-md)]">
            <div className="relative h-40 overflow-hidden">
              <img
                src="/img/serenity.jpg"
                alt="Momento de paz y reflexión"
                loading="lazy"
                className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
              />
              <span className="absolute bottom-3 left-4 flex h-10 w-10 items-center justify-center rounded-full bg-card text-primary shadow-[var(--shadow-sm)]">
                <Heart className="h-5 w-5" />
              </span>
            </div>
            <CardContent className="flex flex-col gap-3 p-6">
              <h3 className="font-serif text-lg font-semibold">Tus favoritos</h3>
              <p className="text-sm text-muted-foreground">
                {user ? "Guarda los mensajes que más te fortalecen para volver a leerlos." : "Crea tu cuenta para guardar mensajes, comentar y publicar peticiones."}
              </p>
              <Link to={user ? "/favoritos" : "/registro"} className="inline-flex items-center gap-1 text-sm font-semibold text-primary hover:text-primary/80" data-testid="home-favoritos-link">
                {user ? "Ver favoritos" : "Crear cuenta"} <ArrowRight className="h-4 w-4" />
              </Link>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Mensajes recientes */}
      {recent.length > 0 && (
        <section className="mx-auto w-full max-w-6xl px-4 pb-12 sm:px-6 sm:pb-16 lg:px-8">
          <div className="mb-6 flex items-center justify-between">
            <h2 className="font-serif text-2xl font-semibold">Mensajes recientes</h2>
            <Link to="/archivo" className="text-sm font-semibold text-primary hover:text-primary/80" data-testid="home-ver-todos-link">
              Ver todos
            </Link>
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-6 lg:grid-cols-3">
            {recent.map((m) => (
              <MessageCard key={m.id} message={m} />
            ))}
          </div>
        </section>
      )}

      {/* Mision / Sobre el centro */}
      <section className="mx-auto w-full max-w-6xl px-4 pb-12 sm:px-6 sm:pb-16 lg:px-8">
        <div className="grid grid-cols-1 items-center gap-8 overflow-hidden rounded-[var(--radius)] border border-border bg-card shadow-[var(--shadow-sm)] md:grid-cols-2">
          <div className="relative h-64 md:h-full md:min-h-[380px]">
            <img
              src="/img/mission.jpg"
              alt="Montañas al amanecer, símbolo de esperanza"
              loading="lazy"
              className="absolute inset-0 h-full w-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent md:bg-gradient-to-r md:from-transparent md:to-black/10" />
          </div>
          <div className="flex flex-col gap-4 p-6 sm:p-10">
            <span className="inline-flex w-fit items-center gap-2 rounded-full bg-accent px-4 py-1.5 text-xs font-semibold text-primary">
              <Sunrise className="h-4 w-4" /> Nuestra misión
            </span>
            <h2 className="font-serif text-2xl font-semibold sm:text-3xl">
              Una palabra de fortaleza para cada día difícil
            </h2>
            <p className="text-sm leading-relaxed text-muted-foreground sm:text-base">
              Venezuela atraviesa momentos que pesan sobre el corazón. Por eso, cada día publicamos un
              mensaje con análisis bíblico, oración y pasos prácticos, escrito con sensibilidad pastoral
              para acompañarte en medio de la crisis y las secuelas del terremoto.
            </p>
            <p className="text-sm leading-relaxed text-muted-foreground sm:text-base">
              Puedes leer sin registrarte. Y si creas tu cuenta, también podrás compartir testimonios,
              guardar tus mensajes favoritos y unirte al muro de oración.
            </p>
            <div className="mt-2 flex flex-wrap gap-3">
              {!user && (
                <Button onClick={() => navigate("/registro")} data-testid="home-mission-register-button">
                  Crear cuenta gratis
                </Button>
              )}
              <Button variant="outline" onClick={() => navigate("/archivo")} data-testid="home-mission-archivo-button">
                Explorar mensajes
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonios recientes */}
      {testimonios.length > 0 && (
        <section className="gradient-breeze relative">
          <div className="mx-auto w-full max-w-6xl px-4 py-12 sm:px-6 sm:py-16 lg:px-8">
            <div className="mb-6 flex items-center gap-2">
              <MessageCircleHeart className="h-6 w-6 text-primary" />
              <h2 className="font-serif text-2xl font-semibold">Palabras de la comunidad</h2>
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {testimonios.map((c) => (
                <Card key={c.id} className="border-border bg-card shadow-[var(--shadow-sm)]">
                  <CardContent className="flex flex-col gap-2 p-5">
                    <p className="line-clamp-3 text-sm italic leading-relaxed text-foreground/90">“{c.texto}”</p>
                    <div className="flex items-center justify-between text-xs text-muted-foreground">
                      <span className="font-semibold text-foreground">{c.user_name}</span>
                      {c.message_titulo && (
                        <Badge variant="secondary" className="max-w-[180px] truncate bg-accent text-accent-foreground">
                          {c.message_titulo}
                        </Badge>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
